import { prefersReducedMotion } from '../accessibility.js';

/**
 * The CLOSED state of the book.
 *
 * StPageFlip is not involved here, on purpose. It has no concept of a book as
 * a solid: it can only lay pages into a spread area, so it cannot show a page
 * block, a cover sitting proud of that block, or an object with thickness —
 * which is most of what makes the reference photographs read as a real thing.
 * Keeping the cover out of the engine also removes its `showCover` behaviour,
 * where page 0 is forced to hard density (PageCollection.ts:61) and then
 * vanishes at 90 degrees because hard pages have no reverse side.
 *
 * So: this module owns the closed object, the engine owns the interior.
 */

const OPEN_DURATION = 720;
const DRAG_COMMIT = 0.32;

/**
 * @param {object} options
 * @param {HTMLElement} options.host
 * @param {HTMLElement} options.coverPage - the rendered cover article
 * @param {number} options.leafCount - interior leaves, drives block thickness
 * @param {() => void} options.onOpen
 * @param {string} options.openLabel
 */
export function createClosedBook({ host, coverPage, leafCount, onOpen, openLabel }) {
  const reducedMotion = prefersReducedMotion();

  const root = document.createElement('div');
  root.className = 'closed-book';
  // Thickness is a function of how many leaves there are, clamped to the thin
  // editorial booklet in the references rather than a hardback.
  root.style.setProperty('--block-depth', `${Math.min(9, 2 + leafCount * 0.55).toFixed(2)}px`);

  const block = document.createElement('div');
  block.className = 'closed-book__block';
  block.setAttribute('aria-hidden', 'true');

  const cover = document.createElement('div');
  cover.className = 'closed-book__cover';
  cover.append(coverPage);

  // The cover is a real control: it is the only way into the book, so it has
  // to be reachable by keyboard and announce what it does.
  const trigger = document.createElement('button');
  trigger.type = 'button';
  trigger.className = 'closed-book__trigger';
  trigger.textContent = openLabel;

  root.append(block, cover, trigger);
  host.append(root);

  let opening = false;
  let pointerId = null;
  let startX = 0;
  let progress = 0;
  let destroyed = false;

  function setProgress(value) {
    progress = Math.max(0, Math.min(1, value));
    root.style.setProperty('--open-progress', progress.toFixed(4));
  }

  function finishOpen() {
    if (opening || destroyed) return;
    opening = true;
    root.dataset.opening = 'true';

    if (reducedMotion) {
      onOpen();
      return;
    }

    root.style.setProperty('--open-duration', `${OPEN_DURATION}ms`);
    setProgress(1);
    window.setTimeout(() => {
      if (!destroyed) onOpen();
    }, OPEN_DURATION);
  }

  function releaseDrag() {
    if (pointerId === null) return;
    if (root.hasPointerCapture?.(pointerId)) root.releasePointerCapture(pointerId);
    pointerId = null;
    root.dataset.dragging = 'false';
  }

  function onPointerDown(event) {
    if (opening || (event.button !== undefined && event.button !== 0)) return;
    pointerId = event.pointerId;
    startX = event.clientX;
    root.setPointerCapture?.(event.pointerId);
    root.dataset.dragging = 'true';
    root.style.setProperty('--open-duration', '0ms');
  }

  function onPointerMove(event) {
    if (event.pointerId !== pointerId) return;
    // Dragging leftwards lifts the cover, exactly as it will behave once the
    // engine takes over the interior leaves.
    const travel = (startX - event.clientX) / Math.max(120, root.offsetWidth * 0.7);
    setProgress(travel);
  }

  function onPointerUp(event) {
    if (event.pointerId !== pointerId) return;
    releaseDrag();
    root.style.setProperty('--open-duration', `${OPEN_DURATION}ms`);
    if (progress >= DRAG_COMMIT) finishOpen();
    else setProgress(0);
  }

  root.addEventListener('pointerdown', onPointerDown);
  root.addEventListener('pointermove', onPointerMove);
  root.addEventListener('pointerup', onPointerUp);
  root.addEventListener('pointercancel', onPointerUp);
  trigger.addEventListener('click', finishOpen);

  setProgress(0);

  return {
    element: root,
    focus() {
      trigger.focus();
    },
    destroy() {
      destroyed = true;
      releaseDrag();
      root.removeEventListener('pointerdown', onPointerDown);
      root.removeEventListener('pointermove', onPointerMove);
      root.removeEventListener('pointerup', onPointerUp);
      root.removeEventListener('pointercancel', onPointerUp);
      trigger.removeEventListener('click', finishOpen);
      root.remove();
    }
  };
}
